import React from 'react';
import { Box } from '@mui/material';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import { formatTimecode } from '../../utils/timecode';
import { angleName } from './multicamStudioModel';
import { angleColor, playerTokens } from './multicamTheme';

type NamedAngle = { angleKey: string; label?: string | null };

interface ViewerBadgeProps {
  /** The angle on screen. */
  angle: NamedAngle;
  /** The angle the cut shows at the playhead. */
  liveKey: string | null;
  fps: number;
  inPoint: number | null;
  outPoint: number | null;
  /** The audio angle, when it has no footage at the playhead (so there is no sound). */
  silentAudioAngle?: NamedAngle | null;
  /** Replaces the no-sound message (a separate recording isn't an angle). */
  silentAudioText?: string;
}

const pillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1,
  py: 0.5,
  borderRadius: 1.5,
  bgcolor: playerTokens.overlay,
  color: playerTokens.text,
  fontSize: 12,
  fontWeight: 700,
} as const;

/**
 * Over the picture's top-left corner: which angle is on screen (and whether
 * the cut shows it here), the In/Out marks, and a warning where there is no
 * sound because the audio angle did not record this moment.
 */
const ViewerBadge: React.FC<ViewerBadgeProps> = ({ angle, liveKey, fps, inPoint, outPoint, silentAudioAngle, silentAudioText }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
    <Box data-testid="viewer-angle-badge" sx={pillSx}>
      <Box
        component="span"
        aria-hidden="true"
        sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: angleColor(angle.angleKey), boxShadow: `0 0 0 1px ${playerTokens.border}` }}
      />
      {angleName(angle)}
      {angle.angleKey === liveKey && (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: playerTokens.textMuted, fontWeight: 600 }}>
          <Box component="span" aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: playerTokens.live }} />
          Live in cut
        </Box>
      )}
    </Box>
    {(inPoint !== null || outPoint !== null) && (
      <Box sx={{ ...pillSx, fontVariantNumeric: 'tabular-nums' }}>
        {inPoint !== null ? `In ${formatTimecode(inPoint, fps)}` : 'In —'}
        {'  ·  '}
        {outPoint !== null ? `Out ${formatTimecode(outPoint, fps)}` : 'Out —'}
      </Box>
    )}
    {silentAudioAngle && (
      <Box data-testid="audio-gap-badge" sx={{ ...pillSx, gap: 0.5, fontWeight: 600 }}>
        <VolumeOffOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
        {silentAudioText || `No sound here: ${angleName(silentAudioAngle)} has no footage`}
      </Box>
    )}
  </Box>
);

export default ViewerBadge;
